import { Button, Grid, IconButton, TextField } from "@material-ui/core";
import {
  IconBuilding,
  IconEdit,
  IconKey,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import CustomDialog from "../../../components/common/dialogs/CustomDialog";
import { CustomIconButton } from "../../../components/common/icon-buttons/CustomIconButton";
import CustomTable, {
  RowItemType,
} from "../../../components/common/table/CustomTable";
import { Usuario } from "../../../interfaces/usuarios/usuarios.interface";
import {
  useDisableUserMutation,
  useGetUsersQuery,
} from "../../../services/users/usersService";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import ChangePasswordForm from "../forms/ChangePasswordForm";
import ChangeProfileUserForm from "../forms/ChangeProfileUserForm";
import {
  setPageTableUsers,
  setSizeTableUsers,
  setUserToEdit,
} from "../slices/usersSlice";
import EditUserForm from "../forms/EditUserForm";
import AddUserForm from "../forms/AddUserForm";

const TableUsers = () => {
  const [idToChangePass, setIdToChangePass] = useState<number>();
  const [idToChangeRol, setIdToChangeRol] = useState<number>();
  const [idToDisable, setIdToDisable] = useState<number>();
  const [searchUser, setSearchUser] = useState<string>();
  const [userToEditModal, setUserToEditModal] = useState<Usuario>();
  const [showAddForm, setShowAddForm] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const { page, size } = useAppSelector((state) => state.usuarios);
  const dispatch = useAppDispatch();

  const { isLoading, isFetching, data, refetch, isError } = useGetUsersQuery(
    {
      page: page,
      pageSize: size,
      search: searchUser,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [
    disableUser,
    {
      isLoading: isLoadingDisable,
      isError: isErrorDisable,
      isSuccess: isSuccessDisable,
      reset: resetDisable,
    },
  ] = useDisableUserMutation();

  const handleVerEmpresas = async (usuario: Usuario) => {
    await dispatch(setUserToEdit(usuario));
    history.push(`${location.pathname}/clients/`);
  };

  const handleShowDialogChangePass = (id: number) => {
    setIdToChangePass(id);
  };

  const handleShowDialogDisable = (id: number) => {
    setIdToDisable(id);
  };

  const handleDisableUser = () => {
    disableUser({
      id: idToDisable!,
    });
  };

  const handleEditUserModal = (user: Usuario) => {
    setUserToEditModal(user);
  };

  const rows: RowItemType[] =
    data?.usuarios?.map((usu, index) => ({
      number: index + 1 + page * size,
      id: String(usu.id),
      name: usu.nombre?.toUpperCase() || "-",
      cedula: usu.cedula || "-",
      email: usu.email || "-",
      perfil: usu.nombre_perfil || "-",
      options: (
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          {usu.perfil === "2" ? (
            <CustomIconButton
              toolTipText="Ver Empresas"
              size="small"
              icon={<IconBuilding color="dodgerblue" />}
              onClick={() => handleVerEmpresas(usu)}
            />
          ) : (
            <div style={{ width: "2rem" }} />
          )}
          <CustomIconButton
            toolTipText="Cambiar Contraseña"
            size="small"
            icon={<IconKey color="orange" />}
            onClick={() => handleShowDialogChangePass(Number(usu.id!))}
          />
          <CustomIconButton
            toolTipText="Editar Usuario"
            size="small"
            icon={<IconEdit color="navy" />}
            onClick={() => handleEditUserModal(usu)}
          />
          <CustomIconButton
            toolTipText="Deshabilitar"
            size="small"
            icon={<IconTrash color="red" />}
            onClick={() => handleShowDialogDisable(Number(usu.id!))}
          />
        </div>
      ),
    })) || [];

  const headers = [
    {
      id: "number",
      disablePadding: false,
      label: "#",
      numeric: true,
    },
    {
      id: "id",
      disablePadding: false,
      label: "ID",
      numeric: true,
      hidden: true,
    },
    {
      id: "name",
      disablePadding: false,
      label: "NOMBRE",
      numeric: false,
    },
    {
      id: "cedula",
      disablePadding: false,
      label: "CÉDULA",
      numeric: false,
    },
    {
      id: "email",
      disablePadding: false,
      label: "EMAIL",
      numeric: false,
    },
    {
      id: "perfil",
      disablePadding: false,
      label: "PERFIL",
      numeric: false,
    },
    {
      id: "options",
      disablePadding: false,
      label: "OPCIONES",
      numeric: false,
    },
  ];

  const clearSearchUser = () => {
    setSearchUser("");
  };

  useEffect(() => {
    isSuccessDisable && refetch();
  }, [isSuccessDisable, refetch]);

  return (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          variant="outlined"
          fullWidth
          label=" Buscar Usuario"
          placeholder="(Nombre - Correo - Cédula)"
          defaultValue={searchUser}
          value={searchUser}
          onChange={(e) => setSearchUser(e.target.value)}
          InputProps={{
            endAdornment: searchUser && searchUser?.length > 0 && (
              <IconButton children={<IconX />} onClick={clearSearchUser} />
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} md={12} lg={12} style={{ textAlign: "right" }}>
        <Button
          onClick={() => setShowAddForm(true)}
          variant="contained"
          color="primary"
          startIcon={<IconPlus />}
        >
          Crear Usuario
        </Button>
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <CustomTable
          dense={true}
          isLoading={isLoading || isFetching}
          isError={isError}
          rows={rows}
          headers={headers}
          totalRecords={Number(data?.cantidadUsuarios) || 5}
          page={page}
          rowsPerPage={size}
          onChangePage={(page) => {
            dispatch(setPageTableUsers(page));
          }}
          onChangeRowsPerPage={(rowsPerPage) =>
            dispatch(setSizeTableUsers(rowsPerPage))
          }
        />
      </Grid>
      <CustomDialog
        isOpen={Boolean(idToChangePass)}
        handleClose={() => setIdToChangePass(undefined)}
        title="Cambiar Contraseña de Usuario"
        hideActionButtons
      >
        <ChangePasswordForm
          handleClose={() => setIdToChangePass(undefined)}
          refetch={refetch}
          idUser={idToChangePass!}
        />
      </CustomDialog>
      <CustomDialog
        isOpen={Boolean(idToChangeRol)}
        handleClose={() => setIdToChangeRol(undefined)}
        title="Cambiar Perfil de Usuario"
        hideActionButtons
      >
        <ChangeProfileUserForm
          handleClose={() => setIdToChangeRol(undefined)}
          refetch={refetch}
          idUser={idToChangeRol!}
        />
      </CustomDialog>
      {userToEditModal && (
        <CustomDialog
          handleClose={() => setUserToEditModal(undefined)}
          isOpen={Boolean(userToEditModal)}
          title="Editar Usuario"
          hideActionButtons
        >
          <EditUserForm refetchUsers={refetch} userToEdit={userToEditModal} />
        </CustomDialog>
      )}
      <CustomDialog
        isOpen={Boolean(idToDisable)}
        handleClose={() => {
          setIdToDisable(undefined);
          resetDisable();
        }}
        title="¿Deshabilitar Usuario?"
        subtitle="¿Está seguro de deshabilitar el usuario seleccionado?"
        textButton="Sí, Deshabilitar"
        handleSubmit={handleDisableUser}
        isLoading={isLoadingDisable}
        isError={isErrorDisable}
        errorMessage="El servicio no esta disponible por el momento. Intente luego."
        isSuccess={isSuccessDisable}
        successMessage="Se deshabilito el usuario correctamente."
      />
      <CustomDialog
        isOpen={showAddForm}
        handleClose={() => setShowAddForm(false)}
        hideActionButtons
        title="Crear Usuario"
      >
        <AddUserForm refetchList={refetch} />
      </CustomDialog>
    </>
  );
};

export default TableUsers;
